import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Gift, ArrowLeft, Clock, Check, X, ChevronRight, Upload, Loader2, Trophy, User, Crown } from 'lucide-react';

function EarnVotesPage() {
  const [currentUser, setCurrentUser] = useState(null);
  const [earnMethods, setEarnMethods] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [topArtists, setTopArtists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [showProofModal, setShowProofModal] = useState(null);
  const [proofUrl, setProofUrl] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, methodsRes, submissionsRes, leaderboardRes] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: 'include' }),
        fetch(`${API}/earn-methods`, { credentials: 'include' }),
        fetch(`${API}/earned-votes/my-submissions`, { credentials: 'include' }),
        fetch(`${API}/votes/leaderboard`, { credentials: 'include' })
      ]);

      if (!userRes.ok) {
        navigate('/login');
        return;
      }

      const userData = await userRes.json();
      setCurrentUser(userData);

      if (methodsRes.ok) {
        const methodsData = await methodsRes.json();
        setEarnMethods(methodsData);
      }

      if (submissionsRes.ok) {
        const submissionsData = await submissionsRes.json();
        setMySubmissions(submissionsData);
      }

      if (leaderboardRes.ok) {
        const leaderboardData = await leaderboardRes.json();
        // Get top 6 artists
        setTopArtists(leaderboardData.slice(0, 6));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActionClick = async (method) => {
    if (method.requires_approval) {
      setShowProofModal(method);
    } else {
      navigate(method.action_button_link);
    }
  };

  const handleSubmitProof = async () => {
    if (!showProofModal) return;
    
    setSubmitting(showProofModal.method_id);
    try {
      const response = await fetch(`${API}/earn-methods/${showProofModal.method_id}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ proof_url: proofUrl })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Claim submitted for approval!');
        setShowProofModal(null);
        setProofUrl('');
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit claim');
    } finally {
      setSubmitting(null);
    }
  };

  const getPendingSubmission = (methodId) => {
    return mySubmissions.find(s => s.method_id === methodId && s.status === 'pending');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
            <Check className="h-3 w-3" /> Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
            <X className="h-3 w-3" /> Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium">
            <Clock className="h-3 w-3" /> Pending
          </span>
        );
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-yellow-400" />;
    if (rank === 2) return <span className="text-gray-300 font-bold">🥈</span>;
    if (rank === 3) return <span className="text-amber-600 font-bold">🥉</span>;
    return <span className="text-muted-foreground font-bold">#{rank}</span>;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Proof Upload Modal */}
      {showProofModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="glass-panel p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Submit Proof</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowProofModal(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              For: <span className="text-white font-medium">{showProofModal.action}</span>
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              {showProofModal.rules}
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Proof URL or Description</label>
                <input
                  type="text"
                  value={proofUrl}
                  onChange={(e) => setProofUrl(e.target.value)}
                  placeholder="Paste link to your social media post..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                  data-testid="proof-url-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleSubmitProof}
                  disabled={!proofUrl || submitting}
                  className="flex-1 bg-secondary text-black rounded-full"
                  data-testid="submit-proof-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Submit for Approval
                </Button>
                <Button variant="outline" onClick={() => setShowProofModal(null)} className="rounded-full">
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-btn">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 tracking-tight flex items-center gap-3">
            <Gift className="h-8 w-8 md:h-10 md:w-10 text-secondary" />
            <span>Earn More Votes</span>
          </h1>
          <p className="text-muted-foreground text-sm md:text-base">
            Complete tasks to earn free votes! Your balance:{' '}
            <span className="text-secondary font-bold text-lg">{currentUser?.free_votes_remaining || 0}</span> votes
          </p>
        </div>

        {/* Top 6 Artists Section */}
        {topArtists.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                <Trophy className="h-6 w-6 text-yellow-400" />
                Top Artists
              </h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/leaderboard')}
                className="text-primary"
                data-testid="view-leaderboard-btn"
              >
                View Full Leaderboard
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            
            {/* Desktop Grid */}
            <div className="hidden md:grid grid-cols-6 gap-3">
              {topArtists.map((artist, index) => (
                <Card 
                  key={artist.user_id} 
                  className={`glass-panel p-3 text-center cursor-pointer hover:border-primary/50 transition-all ${
                    index === 0 ? 'border-yellow-400/50 bg-yellow-400/5' : ''
                  }`}
                  onClick={() => navigate(`/profile/${artist.user_id}`)}
                  data-testid={`top-artist-${index + 1}`}
                >
                  <div className="flex justify-center mb-2">
                    {getRankBadge(index + 1)}
                  </div>
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-2 overflow-hidden">
                    {artist.picture ? (
                      <img src={artist.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <p className="font-medium text-sm truncate">{artist.stage_name || artist.name}</p>
                  <p className="text-secondary font-bold text-sm">{artist.total_votes || 0}</p>
                  <p className="text-xs text-muted-foreground">votes</p>
                </Card>
              ))}
            </div>

            {/* Mobile Horizontal Scroll */}
            <div className="md:hidden flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {topArtists.map((artist, index) => (
                <Card 
                  key={artist.user_id} 
                  className={`glass-panel p-3 text-center flex-shrink-0 w-28 cursor-pointer ${
                    index === 0 ? 'border-yellow-400/50 bg-yellow-400/5' : ''
                  }`}
                  onClick={() => navigate(`/profile/${artist.user_id}`)}
                  data-testid={`mobile-top-artist-${index + 1}`}
                >
                  <div className="flex justify-center mb-1">
                    {getRankBadge(index + 1)}
                  </div>
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-1 overflow-hidden">
                    {artist.picture ? (
                      <img src={artist.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <p className="font-medium text-xs truncate">{artist.stage_name || artist.name}</p>
                  <p className="text-secondary font-bold text-xs">{artist.total_votes || 0}</p>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Desktop Table View */}
        <div className="hidden md:block mb-8">
          <h2 className="text-xl font-bold mb-4">Ways to Earn Votes</h2>
          <Card className="glass-panel overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="text-left p-4 font-semibold">Action</th>
                    <th className="text-center p-4 font-semibold whitespace-nowrap">Votes Earned</th>
                    <th className="text-left p-4 font-semibold">Rules & Conditions</th>
                    <th className="text-center p-4 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {earnMethods.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="p-8 text-center text-muted-foreground">
                        No earn methods available at the moment.
                      </td>
                    </tr>
                  ) : (
                    earnMethods.map((method, index) => {
                      const pendingSubmission = getPendingSubmission(method.method_id);
                      return (
                        <tr 
                          key={method.method_id} 
                          className={`border-b border-white/10 hover:bg-white/5 transition-colors ${
                            index % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.02]'
                          }`}
                          data-testid={`earn-row-${method.method_id}`}
                        >
                          <td className="p-4">
                            <span className="font-medium">{method.action}</span>
                          </td>
                          <td className="text-center p-4">
                            <span className="inline-flex items-center justify-center px-3 py-1 bg-secondary/20 text-secondary font-bold rounded-full">
                              +{method.votes_earned} {method.votes_earned === 1 ? 'vote' : 'votes'}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-muted-foreground">{method.rules}</span>
                          </td>
                          <td className="text-center p-4">
                            {pendingSubmission ? (
                              getStatusBadge('pending')
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleActionClick(method)}
                                className={`rounded-full ${
                                  method.requires_approval 
                                    ? 'bg-accent hover:bg-accent/80' 
                                    : 'bg-primary hover:bg-primary/80'
                                }`}
                                data-testid={`action-btn-${method.method_id}`}
                              >
                                {method.requires_approval && <Upload className="h-3 w-3 mr-1" />}
                                {method.action_button_text}
                                {!method.requires_approval && <ChevronRight className="h-3 w-3 ml-1" />}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3 mb-8">
          <h2 className="text-lg font-bold">Ways to Earn Votes</h2>
          {earnMethods.length === 0 ? (
            <Card className="glass-panel p-6 text-center">
              <p className="text-muted-foreground">No earn methods available at the moment.</p>
            </Card>
          ) : (
            earnMethods.map((method) => {
              const pendingSubmission = getPendingSubmission(method.method_id);
              return (
                <Card 
                  key={method.method_id} 
                  className="glass-panel p-4 hover:border-primary/30 transition-all"
                  data-testid={`earn-card-${method.method_id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm leading-tight mb-1">{method.action}</h3>
                      <p className="text-xs text-muted-foreground">{method.rules}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center justify-center px-2 py-1 bg-secondary/20 text-secondary font-bold text-xs rounded-full whitespace-nowrap">
                        +{method.votes_earned}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    {pendingSubmission ? (
                      getStatusBadge('pending')
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleActionClick(method)}
                        className={`w-full rounded-full text-sm ${
                          method.requires_approval 
                            ? 'bg-accent hover:bg-accent/80' 
                            : 'bg-primary hover:bg-primary/80'
                        }`}
                        data-testid={`mobile-action-btn-${method.method_id}`}
                      >
                        {method.requires_approval && <Upload className="h-3 w-3 mr-2" />}
                        {method.action_button_text}
                        {!method.requires_approval && <ChevronRight className="h-4 w-4 ml-2" />}
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* My Submissions History */}
        {mySubmissions.length > 0 && (
          <div>
            <h2 className="text-xl md:text-2xl font-bold mb-4">My Submissions</h2>
            
            {/* Desktop Submissions Table */}
            <div className="hidden md:block">
              <Card className="glass-panel overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="text-left p-4 font-semibold">Task</th>
                        <th className="text-center p-4 font-semibold">Votes</th>
                        <th className="text-center p-4 font-semibold">Status</th>
                        <th className="text-left p-4 font-semibold">Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mySubmissions.map((submission) => {
                        const method = earnMethods.find(m => m.method_id === submission.method_id);
                        return (
                          <tr key={submission.submission_id} className="border-b border-white/10 hover:bg-white/5">
                            <td className="p-4">
                              <span className="font-medium">{method?.action || submission.method_id}</span>
                            </td>
                            <td className="text-center p-4">
                              <span className="text-secondary font-bold">+{submission.votes_earned}</span>
                            </td>
                            <td className="text-center p-4">{getStatusBadge(submission.status)}</td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {new Date(submission.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>

            {/* Mobile Submissions Cards */}
            <div className="md:hidden space-y-2">
              {mySubmissions.map((submission) => {
                const method = earnMethods.find(m => m.method_id === submission.method_id);
                return (
                  <Card key={submission.submission_id} className="glass-panel p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{method?.action || submission.method_id}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(submission.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-secondary font-bold text-sm">+{submission.votes_earned}</span>
                        {getStatusBadge(submission.status)}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Info Card */}
        <Card className="glass-panel p-4 md:p-6 mt-6 md:mt-8 border-secondary/30">
          <div className="flex items-start gap-3">
            <Gift className="h-6 w-6 text-secondary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold mb-1">How it works</h3>
              <p className="text-sm text-muted-foreground">
                Complete the actions listed above to earn free votes. Some actions give automatic votes 
                when completed, while others (marked with "Upload Proof") require admin approval. 
                Your earned votes will be added to your balance and can be used to vote for your favorite artists!
              </p>
            </div>
          </div>
        </Card>

        {/* Buy Votes Card - At Bottom */}
        <Card className="glass-panel p-4 md:p-6 mt-6 border-green-500/30 bg-gradient-to-r from-green-500/10 to-transparent">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">💰</span>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Need More Votes?</h3>
                <p className="text-sm text-muted-foreground">
                  Purchase votes instantly to support your favorite artists!
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/buy-votes')}
              className="bg-green-500 hover:bg-green-600 text-white rounded-full px-6 py-2 font-bold w-full md:w-auto"
              data-testid="buy-votes-btn"
            >
              Buy Votes
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default EarnVotesPage;
